import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { sendApplicationNotification } from '../services/emailService';
import { supabase, fromApplicationRow } from '../services/supabase';
import { updateDiscordRoles } from '../services/api';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [action, setAction] = useState('');
  const [message, setMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    let subscription = null;

    const checkAuthAndLoad = async () => {
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/admin/login');
          return;
        }
        await loadApplications();
        subscription = supabase
          .channel('applications-changes')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'applications' }, () => {
            loadApplications();
          })
          .subscribe();
      } else {
        const isLoggedIn = localStorage.getItem('adminLoggedIn');
        const loginTime = localStorage.getItem('adminLoginTime');
        if (!isLoggedIn || !loginTime) {
          navigate('/admin/login');
          return;
        }
        const SESSION_TIMEOUT = 8 * 60 * 60 * 1000;
        if (Date.now() - new Date(loginTime).getTime() > SESSION_TIMEOUT) {
          localStorage.removeItem('adminLoggedIn');
          localStorage.removeItem('adminLoginTime');
          navigate('/admin/login');
          return;
        }
        loadApplications();
      }
    };

    checkAuthAndLoad();

    return () => {
      if (subscription && supabase) supabase.removeChannel(subscription);
    };
  }, [navigate]);

  const loadApplications = async () => {
    if (supabase) {
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .order('submitted_at', { ascending: false });
      if (error) {
        console.error('Failed to load applications:', error);
        return;
      }
      setApplications((data || []).map(fromApplicationRow));
    } else {
      const stored = JSON.parse(localStorage.getItem('applications') || '[]');
      setApplications(stored.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)));
    }
  };

  const handleLogout = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminLoginTime');
    navigate('/admin/login');
  };

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setAdminNotes(application.adminNotes || '');
    setShowModal(true);
  };

  const handleApprove = () => {
    setAction('approve');
    setShowModal(true);
  };

  const handleDeny = () => {
    setAction('deny');
    setShowModal(true);
  };

  const handleSubmitAction = async () => {
    if (!selectedApplication) return;

    const updated = {
      ...selectedApplication,
      status: action,
      adminNotes: adminNotes,
      [action === 'approve' ? 'approvedAt' : 'deniedAt']: new Date().toISOString()
    };

    if (supabase) {
      const { error } = await supabase
        .from('applications')
        .update({
          status: action,
          admin_notes: adminNotes,
          [action === 'approve' ? 'approved_at' : 'denied_at']: new Date().toISOString()
        })
        .eq('id', selectedApplication.id);

      if (error) {
        setMessage('Failed to update application.');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      setApplications(prev =>
        prev.map(app => (app.id === selectedApplication.id ? updated : app))
      );
    } else {
      const updatedApplications = applications.map(app =>
        app.id === selectedApplication.id ? updated : app
      );
      localStorage.setItem('applications', JSON.stringify(updatedApplications));
      setApplications(updatedApplications);
    }

    sendNotification(updated, action);

    let successMessage = `Application ${action === 'approve' ? 'approved' : 'denied'} successfully!`;
    if (updated.discordId) {
      try {
        await updateDiscordRoles({
          discordId: updated.discordId,
          department: updated.department,
          action,
        });
      } catch (discordErr) {
        console.error('Discord role update failed:', discordErr);
        successMessage = `Application ${action === 'approve' ? 'approved' : 'denied'}, but Discord roles could not be updated (${discordErr.message || 'see console'}).`;
      }
    }

    setShowModal(false);
    setSelectedApplication(null);
    setAdminNotes('');
    setAction('');
    setMessage(successMessage);
    setTimeout(() => setMessage(''), updated.discordId ? 6000 : 3000);
  };

  const sendNotification = async (application, action) => {
    try {
      await sendApplicationNotification(application, action);
    } catch (error) {
      console.error('Failed to send notification:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'approve':
        return <Badge bg="success">Approved</Badge>;
      case 'deny':
        return <Badge bg="danger">Denied</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-dashboard">
      <Container fluid className="mt-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="admin-dashboard-title">
                  <i className="fas fa-tachometer-alt me-2"></i>
                  Admin Dashboard
                </h1>
                <p className="text-muted">Skyline RP Application Management</p>
              </div>
              <Button variant="outline-danger" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt me-2"></i>
                Logout
              </Button>
            </div>
          </Col>
        </Row>

        {message && (
          <Alert variant="success" dismissible onClose={() => setMessage('')}>
            {message}
          </Alert>
        )}

        <Row className="mb-4">
          <Col md={3}>
            <Card className="stats-card">
              <Card.Body className="text-center">
                <h3 className="text-primary">{applications.length}</h3>
                <p className="mb-0">Total Applications</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card">
              <Card.Body className="text-center">
                <h3 className="text-warning">
                  {applications.filter(app => app.status === 'pending').length}
                </h3>
                <p className="mb-0">Pending Review</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card">
              <Card.Body className="text-center">
                <h3 className="text-success">
                  {applications.filter(app => app.status === 'approve').length}
                </h3>
                <p className="mb-0">Approved</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card">
              <Card.Body className="text-center">
                <h3 className="text-danger">
                  {applications.filter(app => app.status === 'deny').length}
                </h3>
                <p className="mb-0">Denied</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <div className="d-flex align-items-center gap-2">
              <span className="me-2 fw-bold">Filter:</span>
              <Button variant={filterStatus === 'all' ? 'primary' : 'outline-primary'} size="sm" onClick={() => setFilterStatus('all')}>All</Button>
              <Button variant={filterStatus === 'pending' ? 'warning' : 'outline-warning'} size="sm" onClick={() => setFilterStatus('pending')}>Pending</Button>
              <Button variant={filterStatus === 'approve' ? 'success' : 'outline-success'} size="sm" onClick={() => setFilterStatus('approve')}>Approved</Button>
              <Button variant={filterStatus === 'deny' ? 'danger' : 'outline-danger'} size="sm" onClick={() => setFilterStatus('deny')}>Denied</Button>
            </div>
          </Col>
        </Row>

        <Card className="applications-table-card">
          <Card.Header>
            <h4 className="mb-0">
              <i className="fas fa-list me-2"></i>
              Applications
            </h4>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive className="applications-table mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Discord Username</th>
                  <th>Department</th>
                  <th>Discord User ID</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications
                  .filter(app => filterStatus === 'all' ? true : app.status === filterStatus)
                  .map((application) => (
                    <tr key={application.id}>
                      <td>{formatDate(application.submittedAt)}</td>
                      <td>{application.fullName}</td>
                      <td>{application.characterName}</td>
                      <td>{application.department}</td>
                      <td>{application.discordId}</td>
                      <td>{getStatusBadge(application.status)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewApplication(application)}
                          className="me-2"
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        {application.status === 'pending' && (
                          <>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => {
                                setSelectedApplication(application);
                                handleApprove();
                              }}
                              className="me-2"
                            >
                              <i className="fas fa-check"></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setSelectedApplication(application);
                                handleDeny();
                              }}
                            >
                              <i className="fas fa-times"></i>
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Container>

      {/* Application Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {action ? `${action === 'approve' ? 'Approve' : 'Deny'} Application` : 'Application Details'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApplication && (
            <div>
              <Row>
                <Col md={6}>
                  <h6>Personal Information</h6>
                  <p><strong>Full Name:</strong> {selectedApplication.fullName}</p>
                  <p><strong>Age:</strong> {selectedApplication.age}</p>
                  <p><strong>Email:</strong> {selectedApplication.email}</p>
                </Col>
                <Col md={6}>
                  <h6>Character Information</h6>
                  <p><strong>Discord Username:</strong> {selectedApplication.characterName}</p>
                  <p><strong>Discord User ID:</strong> {selectedApplication.discordId}</p>
                  <p><strong>Department:</strong> {selectedApplication.department}</p>
                </Col>
              </Row>
              
              <hr />
              
              <h6>Experience & Background</h6>
              <p>{selectedApplication.experience}</p>
              
              <hr />
              
              <h6>Application Details</h6>
              <p><strong>Submitted:</strong> {formatDate(selectedApplication.submittedAt)}</p>
              <p><strong>Status:</strong> {getStatusBadge(selectedApplication.status)}</p>
              
              {action && (
                <>
                  <hr />
                  <Form.Group>
                    <Form.Label>Admin Notes (Optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add any notes about this application..."
                    />
                  </Form.Group>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          {action && (
            <Button
              variant={action === 'approve' ? 'success' : 'danger'}
              onClick={handleSubmitAction}
            >
              <i className={`fas fa-${action === 'approve' ? 'check' : 'times'} me-2`}></i>
              {action === 'approve' ? 'Approve' : 'Deny'} Application
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminDashboard; 